import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router";

import {
  OBS_TAG_ORG_ID,
  OBS_TAG_ORG_NAME,
  OBS_TAG_RESOURCE_ID,
  OBS_TAG_RESOURCE_TYPE,
  OBS_TAG_USER_EMAIL,
} from "@superblocksteam/shared";

import {
  getCurrentOrgId,
  getCurrentOrgNameSelector,
} from "legacy/selectors/organizationSelectors";
import { getCurrentUser } from "legacy/selectors/usersSelectors";
import { getCurrentApplication } from "store/slices/application/selectors";

import { getTelemetry } from "./init";
import UITracing from "./UITracing";

export const useTracingManager = () => {
  // manage span attributes
  const user = useSelector(getCurrentUser);
  const organizationId = useSelector(getCurrentOrgId);
  const application = useSelector(getCurrentApplication);
  const organizationName = useSelector(getCurrentOrgNameSelector);
  const attributes = useMemo(
    () => ({
      [OBS_TAG_RESOURCE_ID]: application?.id,
      [OBS_TAG_RESOURCE_TYPE]: "application",
      [OBS_TAG_ORG_ID]: organizationId,
      [OBS_TAG_USER_EMAIL]: user?.email,
      [OBS_TAG_ORG_NAME]: organizationName,
    }),
    [application?.id, organizationId, user?.email, organizationName],
  );

  useEffect(() => {
    UITracing.setGlobalAttributes(attributes);
    // Also push dynamic context into the telemetry package's
    // GlobalAttributeSpanProcessor so auto-instrumented spans
    // (XHR, fetch) carry org/user context. getTelemetry() throws when the
    // module-level init failed (it logs and continues) — telemetry must
    // never crash the component tree, so swallow that case.
    try {
      getTelemetry().setGlobalSpanAttributes(
        Object.fromEntries(
          Object.entries(attributes).filter(
            (entry): entry is [string, string] => entry[1] != null,
          ),
        ),
      );
    } catch {
      // Telemetry disabled — initTelemetry() already logged the failure.
    }
  }, [attributes]);

  // manage sessions based on app id
  const location = useLocation();
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const manageSession = useCallback(() => {
    const matched = matchPath(
      "/applications/:applicationId/*",
      location.pathname,
    );
    const appIdInUrl =
      matched && (matched.params as Record<string, string>).applicationId;
    if (appIdInUrl !== applicationId) {
      if (appIdInUrl) {
        UITracing.startSession(appIdInUrl);
      } else {
        UITracing.endSession();
      }

      setApplicationId(appIdInUrl);
    }
  }, [applicationId, location.pathname]);

  useEffect(() => {
    manageSession();
  }, [manageSession]);
};
